import type { Contract, UnPaidItem } from '@/types/contracts'
import type { Document, UserDocument } from '@/types/documents'
import { type NotificationCount, ProfileType } from '@/types/me'
import type { AllNotifications } from '@/types/notifications'
import type { RequestState } from '@/utils/restStore'
import { RequestStatus, handleRequest, initialRequestState } from '@/utils/restStore'
import { defineStore } from 'pinia'

import Api from '@/services/api'

interface State {
  notificationCount: RequestState<NotificationCount>
  notifications: RequestState<AllNotifications>
}

const initialState = (): State => ({
  notificationCount: initialRequestState(),
  notifications: initialRequestState()
})

export const useNotificationsStore = defineStore({
  id: 'notifications',
  state: initialState,
  actions: {
    async fetchNotificationCount() {
      await handleRequest(Api.getNotificationCount, (count: RequestState<NotificationCount>) => {
        this.notificationCount = count
      })
    },
    async fetchAllNotifications() {
      await handleRequest(Api.getNotifications, (notifications: RequestState<AllNotifications>) => {
        this.notifications = notifications
      })
    }
  },
  getters: {
    extractsAllContractsByProfile:
      (state: State) =>
      (profileType: ProfileType, companyId?: string): Contract[] => {
        const contracts: Contract[] = []
        const pushContract = (contract?: Contract) => {
          if (!contract) return
          if (contracts.some((c) => c.uuid === contract.uuid)) return // avoid duplication
          contracts.push(contract)
        }

        if (state.notifications.status !== RequestStatus.RequestLoaded) {
          return []
        } else if (profileType === ProfileType.B2C) {
          //* handle pending signature contracts
          state.notifications.data.b2c.documents_awaiting_signature
            .filter((document) => document.contract)
            .forEach((document) => pushContract(document.contract as Contract))

          //* handle unpaids contracts
          state.notifications.data.b2c.unpaids.forEach((unpaid) => pushContract(unpaid.contract as Contract))

          //* handle pending upload contracts
          state.notifications.data.b2c.documents_to_upload.forEach((document) =>
            pushContract(document.contractRequest as Contract)
          )
        } else if (profileType === ProfileType.B2B) {
          const company = state.notifications.data.b2b.find((company) => company.company_id === companyId)

          //* handle pending signature contracts
          company?.documents_awaiting_signature
            .filter((document) => document.contract)
            .forEach((document) => pushContract(document.contract as Contract))

          company?.unpaids.forEach((unpaid) => pushContract(unpaid.contract as Contract))
        }

        return contracts
      },
    extractDocumentWithoutContractByProfile:
      (state: State) =>
      (profileType: ProfileType, companyId?: string): Document[] => {
        let documents: Document[] = []

        if (profileType === ProfileType.B2C && state.notifications.status === RequestStatus.RequestLoaded) {
          documents = state.notifications.data.b2c.documents_awaiting_signature.filter((document) => !document.contract)
        } else if (profileType === ProfileType.B2B && state.notifications.status === RequestStatus.RequestLoaded) {
          const company = state.notifications.data.b2b.find((company) => company.company_id === companyId)

          documents = company?.documents_awaiting_signature.filter((document) => !document.contract) || []
        }

        return documents
      },
    getDocumentToSignByContract:
      (state: State) =>
      (contractUuid: string, profileType: ProfileType, companyId?: string): Document[] => {
        if (profileType === ProfileType.B2C && state.notifications.status === RequestStatus.RequestLoaded) {
          return state.notifications.data.b2c.documents_awaiting_signature.filter(
            (document) => document.contract && document.contract.uuid === contractUuid
          )
        } else if (profileType === ProfileType.B2B && state.notifications.status === RequestStatus.RequestLoaded) {
          const company = state.notifications.data.b2b.find((company) => company.company_id === companyId)

          return (
            company?.documents_awaiting_signature.filter(
              (document) => document.contract && document.contract.uuid === contractUuid
            ) || []
          )
        } else {
          return []
        }
      },
    getDocumentsToUploadByContract:
      (state: State) =>
      (contractUuid: string): UserDocument[] => {
        if (state.notifications.status === RequestStatus.RequestLoaded) {
          // only existing for b2c
          return state.notifications.data.b2c.documents_to_upload.filter(
            (document) => document.contractRequest.uuid === contractUuid
          )
        } else {
          return []
        }
      },
    getUnpaidsByContract:
      (state: State) =>
      (contractUuid: string, profileType: ProfileType = ProfileType.B2C, companyId?: string): UnPaidItem[] => {
        if (state.notifications.status !== RequestStatus.RequestLoaded) return []

        const unpaids =
          profileType === ProfileType.B2B
            ? state.notifications.data.b2b.find((company) => company.company_id === companyId)?.unpaids || []
            : state.notifications.data.b2c.unpaids

        return unpaids.filter((unpaid) => unpaid.contract.uuid === contractUuid)
      },
    getTotalActionsByContract:
      (state: State) =>
      (contractUuid: string, profileType: ProfileType, companyId?: string): number => {
        let n = 0
        if (state.notifications.status === RequestStatus.RequestLoaded && profileType === ProfileType.B2C) {
          state.notifications.data.b2c.documents_to_upload
            .filter((document) => document.contractRequest.uuid === contractUuid)
            .forEach((user) => {
              n += user.documents.length
            })

          n += state.notifications.data.b2c.unpaids.filter((unpaid) => unpaid.contract.uuid === contractUuid).length
          n += state.notifications.data.b2c.documents_awaiting_signature.filter(
            (document) => document.contract && document.contract.uuid === contractUuid
          ).length
        } else if (state.notifications.status === RequestStatus.RequestLoaded && profileType === ProfileType.B2B) {
          const company = state.notifications.data.b2b.find((company) => company.company_id === companyId)
          if (company) {
            n += company.documents_awaiting_signature.filter(
              (document) => document.contract && document.contract.uuid === contractUuid
            ).length
            n += company.unpaids.filter((unpaid) => unpaid.contract.uuid === contractUuid).length
          }
        }

        return n
      }
  }
})
